/**
 * A line-level diff a reviewer can actually read.
 *
 * WHY A NAIVE LINE-BY-LINE LCS IS WRONG FOR THIS SCREEN
 *   Split both sides into lines, find the longest common subsequence, and print
 *   what is left — that is the obvious implementation, and on the documents this
 *   screen reviews it is unusable. `SKILL.md` is prose: one instruction is a
 *   paragraph, and a paragraph is wrapped to whatever width the author happened
 *   to have. Re-wrapping one paragraph moves every one of its line breaks while
 *   leaving every word exactly where it was, so an LCS over LINES reports the
 *   whole paragraph as `N` deletions followed by `N` insertions. A reviewer who
 *   asks "what changed in this file?" gets an answer that says the file was
 *   rewritten, and the one edit that actually happened is buried in the noise.
 *   On a review screen the diff IS the evidence, and evidence that cannot be read
 *   is not evidence.
 *
 * WHY NORMALISING EACH LINE DOES NOT FIX IT
 *   The tempting repair is to decide line identity on whitespace-collapsed text.
 *   It does not work, and the reason is worth recording because the repair looks
 *   correct. Collapsing whitespace makes `one two three` and `one   two three`
 *   the same line — which is right — but a re-wrap moves WORDS BETWEEN lines, not
 *   whitespace within one. The line `Include the transaction id.` becomes the tail
 *   of the previous line, and no per-line normalisation can match a line against
 *   half of itself. An implementation that believes it has solved the reflow case
 *   is comparing keys that were never going to match, and the test that "proves"
 *   it passes because the search underneath is reporting nothing at all.
 *
 * WHY THE UNIT IS A PARAGRAPH, NOT A LINE
 *   Line breaks inside a paragraph are not content in markdown. They are where an
 *   editor wrapped. So the unit of comparison is a PARAGRAPH — a run of
 *   consecutive non-blank lines — and a paragraph's identity is the sequence of
 *   words it contains, joined by single spaces. Re-wrapping a paragraph changes
 *   none of those words, so the unit is equal and the re-wrap is invisible. Edit
 *   a word inside it and the unit differs, and the edit is then reported at word
 *   level inside the paragraph, which is where the reviewer is looking.
 *
 *   Three things are deliberately NOT folded into a paragraph, because in each
 *   case the line break or the spacing is the content:
 *     - a blank line, which is a structural separator in markdown and a real
 *       addition or deletion when it appears or disappears;
 *     - a fenced code block, where indentation and alignment are the code;
 *     - a heading or a list item, where the leading marker changes what the line
 *       is rather than how it is wrapped.
 *
 * WHY AN LCS TABLE OVER UNITS IS NOT THE O(N·M) RISK IT LOOKS LIKE
 *   The quadratic table is a problem over lines, where a 256 KiB document is
 *   thousands of lines. Over units it is a table over paragraphs, and a document
 *   that large has hundreds. `MAX_UNITS` bounds it regardless: past that the two
 *   sides share too little structure for any alignment to be worth the memory,
 *   and the honest answer is "everything between the common ends is new", which
 *   is what the fallback says. The common prefix and suffix are trimmed before
 *   the table is built, so the table is sized by the part that moved.
 *
 * WHY A CHANGED PARAGRAPH IS SHOWN AS DELETIONS AND ADDITIONS
 *   Inside a replaced region, a deletion and an insertion are matched by word
 *   overlap so the reader is told that this text *became* that text, and a pair
 *   is still rendered as two rows — one deletion, one insertion — each carrying
 *   the word-level marks. Printing a pair as a single row would give an added
 *   line and a removed line the same sign, which is the ambiguity UI-01 forbids.
 *   A deletion left with nothing similar enough stays a plain deletion: inventing
 *   a correspondence the author did not make is worse than admitting there was
 *   none.
 *
 * WHAT A DIFF ROW NEVER MEANS BY COLOUR ALONE
 *   Nothing here is colour. `signOf` is a character, the line numbers are
 *   numbers, and `opLabel` states the operation in words. Delete the stylesheet
 *   and the tint goes; no information goes with it.
 */

/** One run of text in a row: in both sides, only new, or only old. */
export type DiffSegmentKind = 'same' | 'added' | 'removed';

export interface DiffSegment {
  readonly kind: DiffSegmentKind;
  readonly text: string;
}

export type DiffOp = 'equal' | 'added' | 'removed';

export interface DiffRow {
  readonly op: DiffOp;
  /** 1-based line number in the old side, or null when the row has no old line. */
  readonly beforeLine: number | null;
  /** 1-based line number in the new side, or null when the row has no new line. */
  readonly afterLine: number | null;
  readonly text: string;
  /**
   * Word-level marks, or null when the row has no counterpart.
   *
   * A deletion that was paired with an insertion carries `same`/`removed`; its
   * counterpart carries `same`/`added`. The two are one diff seen from each side,
   * so a row rendered without them would hide which words moved.
   */
  readonly segments: readonly DiffSegment[] | null;
  /** The pair this row belongs to, shared by both sides, or null. */
  readonly pair: number | null;
  /**
   * True when this row's words are unchanged and only its whitespace is not.
   *
   * The row still renders as unchanged — a reviewer looking for edits must not
   * be shown a paragraph that did not change — but the flag lets the screen
   * count these and say so, rather than leaving a reviewer to wonder why the
   * line numbers shifted under a diff that claims nothing changed.
   */
  readonly whitespaceOnly: boolean;
}

export interface DiffStats {
  /** Lines only in the new document. */
  readonly added: number;
  /** Lines only in the old document. */
  readonly removed: number;
  /** Lines present in both. */
  readonly equal: number;
  /**
   * Paragraphs whose WORDS did not change but whose exact text did.
   *
   * Named for what it is rather than for whether it is interesting: either the
   * line breaks moved or the spacing inside them did, and this build cannot
   * tell those apart because they are the same edit. It is reported rather than
   * swallowed, because an author who deliberately re-indented a paragraph has
   * made a change and deserves to see that it registered.
   */
  readonly whitespaceOnly: number;
}

export interface DiffHunk {
  readonly rows: readonly DiffRow[];
  /** Identical lines collapsed before this hunk. */
  readonly omittedBefore: number;
}

export interface LineDiff {
  readonly hunks: readonly DiffHunk[];
  readonly stats: DiffStats;
  /** True when a bound stopped the alignment or the row budget ran out. */
  readonly bounded: boolean;
  /** Why it was bounded, in words a person can read. */
  readonly boundReason: string | null;
}

/** Identical lines kept either side of a change before the gap is collapsed. */
export const CONTEXT_LINES = 3;

/**
 * Units one side past which an alignment is refused rather than approximated.
 *
 * Chosen so the LCS table stays in a browser's budget on a 256 KiB document: a
 * few thousand paragraphs is far beyond any real skill.
 */
export const MAX_UNITS = 4000;

/** Rows rendered before the rest is withheld. Two unrelated 256 KiB files are large. */
export const MAX_ROWS = 4000;

/** Word overlap at or above which a deletion and an insertion are the same edit. */
const PAIR_THRESHOLD = 0.4;

/** Beyond this many token pairs a word diff costs more than it is worth. */
const WORD_CELLS = 250_000;

interface Line {
  readonly text: string;
  /** What line identity is decided on. Computed once, in `linesOf`. */
  readonly key: string;
  /** Blank separators, headings and list items are never folded into a paragraph. */
  readonly standalone: boolean;
  /** Inside a fenced code block, where every character is content. */
  readonly verbatim: boolean;
}

interface Unit {
  readonly from: number;
  readonly to: number;
  /** Identity: the words, in order, joined by single spaces. */
  readonly key: string;
}

const FENCE = /^\s*(?:```|~~~)/;
const HEADING = /^\s{0,3}#{1,6}\s/;
const LIST_ITEM = /^\s{0,3}(?:[-*+]|\d+[.)])\s/;

/**
 * Split a document into lines and mark the ones that are not prose.
 *
 * A single trailing newline does not produce a final empty line — `a\nb\n` is two
 * lines, not three — because a file that ends with a newline and one that does
 * not are the same document to a reader, and a phantom third line makes every
 * save look like it appended something at the end.
 */
function linesOf(source: string): readonly Line[] {
  if (source === '') return [];
  const split = source.split('\n');
  if (split.length > 1 && split[split.length - 1] === '') split.pop();
  let fence: string | null = null;
  return split.map((text) => {
    const fenceMatch = FENCE.exec(text);
    if (fenceMatch !== null) {
      // A fence opened with ``` is closed by ``` and not by ~~~.
      const marker = fenceMatch[0].trim().slice(0, 3);
      fence = fence === null ? marker : fence === marker ? null : fence;
      return verbatimLine(text);
    }
    // Inside a fence every character is content, so the key is the text itself.
    if (fence !== null) return verbatimLine(text);
    return {
      text,
      standalone: text.trim() === '' || HEADING.test(text) || LIST_ITEM.test(text),
      verbatim: false,
      key: text.replace(/\s+/g, ' ').trim(),
    };
  });
}

/**
 * A line whose identity is its exact bytes.
 *
 * Inside a fenced code block a space is an indent and a tab is a tab; collapsing
 * whitespace there would make `    return 1` and `  return 1` the same line and
 * report an indentation change as no change at all. The key is computed ONCE,
 * here, rather than again when lines are grouped — computing it twice is how the
 * verbatim rule got lost the first time.
 */
function verbatimLine(text: string): Line {
  return { text, standalone: true, verbatim: true, key: text };
}

/** The words of a line, with the whitespace between them discarded. */
function words(text: string): readonly string[] {
  return text.trim().split(/\s+/).filter((word) => word !== '');
}

/**
 * Group lines into units: a run of prose lines is one paragraph; anything marked
 * `standalone` is a unit of its own.
 */
function unitsOf(lines: readonly Line[]): readonly Unit[] {
  const units: Unit[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index] as Line;
    if (line.standalone) {
      units.push({ from: index, to: index, key: line.key });
      index += 1;
      continue;
    }
    const start = index;
    let collected: string[] = [];
    while (index < lines.length) {
      const current = lines[index] as Line;
      if (current.standalone) break;
      collected.push(...words(current.text));
      index += 1;
    }
    units.push({ from: start, to: index - 1, key: collected.join(' ') });
  }
  return units;
}

/** The text of a unit, lines joined by newlines. */
function unitText(unit: Unit, lines: readonly Line[]): string {
  const parts: string[] = [];
  for (let i = unit.from; i <= unit.to; i += 1) parts.push((lines[i] as Line).text);
  return parts.join('\n');
}

/** Split text into words and the whitespace between them, keeping both. */
function tokens(text: string): readonly string[] {
  return text.match(/\s+|[^\s]+/g) ?? [];
}

/**
 * Word overlap as a Jaccard coefficient over SETS.
 *
 * Sets, not multisets: a paragraph that repeats "the" is not more similar for
 * it, and counting duplicates would let boilerplate pair with anything.
 */
function similarity(left: readonly string[], right: readonly string[]): number {
  if (left.length === 0 || right.length === 0) return 0;
  const a = new Set(left);
  const b = new Set(right);
  let shared = 0;
  for (const token of a) if (b.has(token)) shared += 1;
  const union = a.size + b.size - shared;
  return union === 0 ? 0 : shared / union;
}

/**
 * Word-level marks for a replaced pair of texts, as `same`/`added`/`removed`
 * segments.
 *
 * A plain LCS table over tokens: these are the words of one unit, short enough
 * that the quadratic table is not a risk, and a token LCS is what makes "one
 * word changed" read as one word rather than as a whole rewritten paragraph.
 * Over `WORD_CELLS` it declines rather than guessing — a word diff too large to
 * compute is not one to approximate, and the caller renders the pair as a plain
 * deletion and insertion instead.
 */
function wordDiff(before: string, after: string): readonly DiffSegment[] | null {
  const a = tokens(before);
  const b = tokens(after);
  if (a.length * b.length > WORD_CELLS) return null;
  const table: number[][] = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      table[i]![j] = (a[i] === b[j] ? table[i + 1]![j + 1]! + 1 : Math.max(table[i + 1]![j]!, table[i]![j + 1]!)) as number;
    }
  }
  const segments: DiffSegment[] = [];
  const push = (kind: DiffSegmentKind, text: string): void => {
    if (text === '') return;
    const last = segments[segments.length - 1];
    if (last !== undefined && last.kind === kind) segments[segments.length - 1] = { kind, text: last.text + text };
    else segments.push({ kind, text });
  };
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      push('same', a[i] as string);
      i += 1;
      j += 1;
    } else if ((table[i + 1]![j] as number) >= (table[i]![j + 1] as number)) {
      push('removed', a[i] as string);
      i += 1;
    } else {
      push('added', b[j] as string);
      j += 1;
    }
  }
  while (i < a.length) push('removed', a[i++] as string);
  while (j < b.length) push('added', b[j++] as string);
  return segments;
}

interface Step {
  readonly op: 'equal' | 'added' | 'removed';
  readonly a: number;
  readonly b: number;
}

/**
 * The alignment of two unit sequences, as an edit script.
 *
 * The common prefix and suffix are trimmed first, so the table is sized by the
 * part that moved rather than by the whole document.
 */
function align(a: readonly Unit[], b: readonly Unit[]): { steps: readonly Step[]; bounded: boolean } {
  const steps: Step[] = [];
  let start = 0;
  while (start < a.length && start < b.length && a[start]!.key === b[start]!.key) start += 1;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1]!.key === b[endB - 1]!.key) {
    endA -= 1;
    endB -= 1;
  }
  for (let i = 0; i < start; i += 1) steps.push({ op: 'equal', a: i, b: i });

  const n = endA - start;
  const m = endB - start;
  if (n > 0 || m > 0) {
    if (n > MAX_UNITS || m > MAX_UNITS) {
      for (let i = 0; i < n; i += 1) steps.push({ op: 'removed', a: start + i, b: 0 });
      for (let j = 0; j < m; j += 1) steps.push({ op: 'added', a: 0, b: start + j });
    } else {
      const table: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
      for (let i = n - 1; i >= 0; i -= 1) {
        for (let j = m - 1; j >= 0; j -= 1) {
          table[i]![j] =
            a[start + i]!.key === b[start + j]!.key
              ? (table[i + 1]![j + 1] as number) + 1
              : Math.max(table[i + 1]![j] as number, table[i]![j + 1] as number);
        }
      }
      let i = 0;
      let j = 0;
      while (i < n && j < m) {
        if (a[start + i]!.key === b[start + j]!.key) {
          steps.push({ op: 'equal', a: start + i, b: start + j });
          i += 1;
          j += 1;
        } else if ((table[i + 1]![j] as number) >= (table[i]![j + 1] as number)) {
          steps.push({ op: 'removed', a: start + i, b: 0 });
          i += 1;
        } else {
          steps.push({ op: 'added', a: 0, b: start + j });
          j += 1;
        }
      }
      while (i < n) steps.push({ op: 'removed', a: start + i++, b: 0 });
      while (j < m) steps.push({ op: 'added', a: 0, b: start + j++ });
    }
  }

  for (let k = 0; k < a.length - endA; k += 1) steps.push({ op: 'equal', a: endA + k, b: endB + k });
  return { steps, bounded: n > MAX_UNITS || m > MAX_UNITS };
}

/**
 * The diff between two documents, as hunks of rows with the identical runs
 * between them collapsed.
 *
 * `maxRows` exists because this runs on a review screen. The bound is reported,
 * never silent: the difference between "you cannot see the whole diff" and "this
 * diff is truncated, and this is why" is the difference between a screen you can
 * trust and one you cannot. The counts in `stats` always describe the DOCUMENTS,
 * never the rows that survived the budget.
 */
export function diffLines(
  before: string,
  after: string,
  options: { readonly context?: number; readonly maxRows?: number } = {},
): LineDiff {
  const context = options.context ?? CONTEXT_LINES;
  const maxRows = options.maxRows ?? MAX_ROWS;
  const oldLines = linesOf(before);
  const newLines = linesOf(after);
  const oldUnits = unitsOf(oldLines);
  const newUnits = unitsOf(newLines);
  const { steps, bounded: alignmentBounded } = align(oldUnits, newUnits);
  const lines = (unit: Unit): number => unit.to - unit.from + 1;

  let added = 0;
  let removed = 0;
  let equal = 0;
  let whitespaceOnly = 0;
  for (const step of steps) {
    if (step.op === 'added') added += lines(newUnits[step.b] as Unit);
    else if (step.op === 'removed') removed += lines(oldUnits[step.a] as Unit);
    else {
      const oldUnit = oldUnits[step.a] as Unit;
      const newUnit = newUnits[step.b] as Unit;
      equal += Math.max(lines(oldUnit), lines(newUnit));
      // The words matched, which is why the units aligned at all. If the exact
      // text still differs then only whitespace moved — either the line breaks
      // or the spacing inside them. It is counted and reported rather than
      // dropped, because an author who deliberately changed a paragraph's
      // indentation has made a change and deserves to see that it registered.
      if (unitText(oldUnit, oldLines) !== unitText(newUnit, newLines)) whitespaceOnly += 1;
    }
  }
  const stats: DiffStats = { added, removed, equal, whitespaceOnly };

  const changedAt: number[] = [];
  steps.forEach((step, index) => {
    if (step.op !== 'equal') changedAt.push(index);
  });
  if (changedAt.length === 0) {
    return {
      hunks: [],
      stats,
      bounded: alignmentBounded,
      boundReason: alignmentBounded ? unitBoundReason(MAX_UNITS) : null,
    };
  }

  // Changed steps, grouped into regions separated by more than two contexts'
  // worth of unchanged steps — between two such regions there is nothing to show.
  const regions: { from: number; to: number }[] = [];
  let regionStart = changedAt[0] as number;
  let previous = regionStart;
  for (const index of changedAt.slice(1)) {
    if (index - previous > context * 2) {
      regions.push({ from: regionStart, to: previous });
      regionStart = index;
    }
    previous = index;
  }
  regions.push({ from: regionStart, to: previous });

  const hunks: DiffHunk[] = [];
  let cursor = 0;
  let rowsEmitted = 0;
  let bounded = alignmentBounded;
  let boundReason: string | null = bounded ? unitBoundReason(MAX_UNITS) : null;

  for (const region of regions) {
    if (bounded) break;
    const from = Math.max(0, region.from - context);
    // Context on BOTH sides. Leading context alone leaves the line immediately
    // after the last edit off the screen, so a reviewer reading the bottom of a
    // hunk sees the change with nothing after it to show what it changed into.
    const to = Math.min(steps.length - 1, region.to + context);
    const omittedBefore = from - cursor;
    const partners = pairRegion(steps, region, oldUnits, newUnits, oldLines, newLines);
    const rows: DiffRow[] = [];

    for (let index = from; index <= to; index += 1) {
      const step = steps[index] as Step;
      if (step.op === 'equal') {
        rows.push(...equalRows(oldUnits[step.a] as Unit, newUnits[step.b] as Unit, oldLines, newLines));
      } else if (step.op === 'removed') {
        rows.push(...sideRows('removed', oldUnits[step.a] as Unit, oldLines, partnerText(partners.oldToNew, step.a, newUnits, newLines)));
      } else {
        rows.push(...sideRows('added', newUnits[step.b] as Unit, newLines, partnerText(partners.newToOld, step.b, oldUnits, oldLines)));
      }
    }

    const room = maxRows - rowsEmitted;
    if (room <= 0) {
      bounded = true;
      boundReason = rowBudgetReason(maxRows);
      break;
    }
    if (rows.length > room) {
      rows.length = room;
      bounded = true;
      boundReason = rowBudgetReason(maxRows);
    }
    rowsEmitted += rows.length;
    hunks.push({ rows, omittedBefore });
    cursor = to + 1;
  }

  return { hunks, stats, bounded, boundReason };
}

function unitBoundReason(maxUnits: number): string {
  return (
    `These documents have more than ${maxUnits} sections between them, so this diff shows ` +
    'everything as replaced rather than guessing where each part lines up.'
  );
}

function rowBudgetReason(maxRows: number): string {
  return (
    `This diff is longer than ${maxRows} lines, so only the first ${maxRows} are shown. ` +
    'The counts above describe the whole change, not the part kept.'
  );
}

/**
 * The rows for an unchanged unit.
 *
 * When the two sides have the same number of lines the correspondence is exact
 * and both numbers are stated. When the paragraph was re-wrapped there is no
 * 1:1 correspondence left — that is the entire point — so the new side's lines
 * are shown with no old line number and the row says it was re-wrapped. Giving
 * them numbers that do not line up would be a claim the diff cannot support.
 */
function equalRows(
  oldUnit: Unit,
  newUnit: Unit,
  oldLines: readonly Line[],
  newLines: readonly Line[],
): readonly DiffRow[] {
  const oldCount = oldUnit.to - oldUnit.from + 1;
  const newCount = newUnit.to - newUnit.from + 1;
  const sameShape = oldCount === newCount;
  const whitespaceOnly = unitText(oldUnit, oldLines) !== unitText(newUnit, newLines);
  const rows: DiffRow[] = [];
  for (let k = 0; k < newCount; k += 1) {
    const line = newLines[newUnit.from + k];
    if (line === undefined) continue;
    rows.push({
      op: 'equal',
      beforeLine: sameShape ? oldUnit.from + k + 1 : null,
      afterLine: newUnit.from + k + 1,
      text: line.text,
      segments: null,
      pair: null,
      whitespaceOnly,
    });
  }
  return rows;
}

/** The rows for one side of a replaced unit, with the other side's text if it paired. */
function sideRows(
  op: 'added' | 'removed',
  unit: Unit,
  lines: readonly Line[],
  partner: string | null,
): readonly DiffRow[] {
  const rows: DiffRow[] = [];
  for (let i = unit.from; i <= unit.to; i += 1) {
    const line = lines[i];
    if (line === undefined) continue;
    // The pair id is the line's own index, so the two sides agree on it without
    // a counter: row 12 of the old paragraph pairs with row 12 of the new one.
    const pair = partner === null ? null : i + 1;
    const marks = partner === null ? null : wordDiff(op === 'removed' ? line.text : partner, op === 'removed' ? partner : line.text);
    rows.push({
      op,
      beforeLine: op === 'removed' ? i + 1 : null,
      afterLine: op === 'added' ? i + 1 : null,
      text: line.text,
      segments: marks,
      pair,
      whitespaceOnly: false,
    });
  }
  return rows;
}

function partnerText(
  map: ReadonlyMap<number, number>,
  index: number,
  otherUnits: readonly Unit[],
  otherLines: readonly Line[],
): string | null {
  const other = map.get(index);
  if (other === undefined) return null;
  const unit = otherUnits[other];
  return unit === undefined ? null : unitText(unit, otherLines);
}

/**
 * Match the deleted units of one replaced region with the inserted ones.
 *
 * Greedy best-overlap first: the highest-scoring pairs are taken, then the next,
 * so a paragraph that became two paragraphs pairs its first half with the first
 * rather than with whichever happens to share the most common words. A unit left
 * unmatched stays a plain deletion or insertion. Inventing a correspondence the
 * author did not make is worse than admitting there was none, and the threshold
 * is what stops a paragraph about payments pairing with one about invoices.
 */
function pairRegion(
  steps: readonly Step[],
  region: { from: number; to: number },
  oldUnits: readonly Unit[],
  newUnits: readonly Unit[],
  oldLines: readonly Line[],
  newLines: readonly Line[],
): { oldToNew: Map<number, number>; newToOld: Map<number, number> } {
  const oldToNew = new Map<number, number>();
  const newToOld = new Map<number, number>();
  const removals: number[] = [];
  const additions: number[] = [];
  for (let index = region.from; index <= region.to; index += 1) {
    const step = steps[index] as Step;
    if (step.op === 'removed') removals.push(step.a);
    else if (step.op === 'added') additions.push(step.b);
  }
  if (removals.length === 0 || additions.length === 0) return { oldToNew, newToOld };

  const candidates: { old: number; added: number; score: number }[] = [];
  for (const a of removals) {
    const oldText = unitText(oldUnits[a] as Unit, oldLines);
    for (const b of additions) {
      const newText = unitText(newUnits[b] as Unit, newLines);
      const score = similarity(words(oldText), words(newText));
      if (score >= PAIR_THRESHOLD) candidates.push({ old: a, added: b, score });
    }
  }
  candidates.sort((left, right) => right.score - left.score || left.old - right.old || left.added - right.added);
  for (const { old, added } of candidates) {
    if (oldToNew.has(old) || newToOld.has(added)) continue;
    oldToNew.set(old, added);
    newToOld.set(added, old);
  }
  return { oldToNew, newToOld };
}

/** The character that states a row's operation in text. */
export function signOf(op: DiffOp): string {
  if (op === 'added') return '+';
  if (op === 'removed') return '-';
  return ' ';
}

/** The operation in words, for a row's accessible name. */
export function opLabel(op: DiffOp): string {
  if (op === 'added') return 'Added';
  if (op === 'removed') return 'Removed';
  return 'Unchanged';
}